// The training streak: days the player did the daily exam, passed or not.
// One rest day between training days keeps it going (like a real training
// week); two missed days in a row end it. Pure functions over Save.train.

import { dayNumber, msToMidnight } from '@/core/dates';
import type { Save } from './save';

/** Streak lengths that earn a hanko stamp on the result card. */
export const MILESTONES: readonly number[] = [3, 7, 14, 30, 50, 100];

/** How many days apart two training days may be and still count as in a row. */
const MAX_GAP = 2;

export interface Training {
  /** False if this day had already been counted (or lies before the last one). */
  counted: boolean;
  streak: number;
  /** Days since the previous training day; null on the very first. */
  gap: number | null;
  milestone: number | null;
}

/** Count `date` (YYYY-MM-DD) as a training day. */
export function recordTraining(save: Save, date: string): Training {
  const t = save.train;
  const last = t.days.at(-1);
  if (last !== undefined && date <= last) {
    return { counted: false, streak: t.streak, gap: null, milestone: null };
  }
  const gap = last === undefined ? null : dayNumber(date) - dayNumber(last);
  t.streak = gap !== null && gap <= MAX_GAP ? t.streak + 1 : 1;
  t.best = Math.max(t.best, t.streak);
  t.total++;
  t.days = [...t.days, date].slice(-7);
  return {
    counted: true,
    streak: t.streak,
    gap,
    milestone: MILESTONES.includes(t.streak) ? t.streak : null,
  };
}

/** none: never trained; done: trained today; open: today's still to do;
 *  risk: the rest day is used up, train today or it ends; lost: it ended. */
export type StreakState = 'none' | 'done' | 'open' | 'risk' | 'lost';

export function streakState(save: Save, today: string): { streak: number; state: StreakState } {
  const t = save.train;
  const last = t.days.at(-1);
  if (last === undefined) return { streak: 0, state: 'none' };
  const gap = dayNumber(today) - dayNumber(last);
  if (gap <= 0) return { streak: t.streak, state: 'done' };
  if (gap === 1) return { streak: t.streak, state: 'open' };
  if (gap === MAX_GAP) return { streak: t.streak, state: 'risk' };
  return { streak: 0, state: 'lost' };
}

/** The last seven days, ending today, and whether each was a training day. */
export function weekStrip(save: Save, today: string): { day: number; done: boolean }[] {
  const end = dayNumber(today);
  const trained = new Set(save.train.days.map(dayNumber));
  return Array.from({ length: 7 }, (_, i) => {
    const day = end - 6 + i;
    return { day, done: trained.has(day) };
  });
}

/** Hours and minutes until the next daily exam (local midnight), minutes rounded up. */
export function countdown(now: Date = new Date()): { h: number; m: number } {
  const minutes = Math.ceil(msToMidnight(now) / 60_000);
  return { h: Math.floor(minutes / 60), m: minutes % 60 };
}
